import { countingEdits } from '../components/overlay/corrections';
import { editRow } from '../components/overlay/changeWords';
import { filterByConfidence, indexTracks, lineCrossings } from '../components/overlay/tracksModel';
import { clock } from './reportText';

/**
 * Page 3 of the report, "Corrections": the steps from what the analysis found to the report count, and a row for each
 * edit that counts. The steps start at the tracks (a person never sees the job's stored count).
 */

// rows of the table on the page under the summary, and on a page of the table alone (ReportDocument geometry)
export const FIRST_PAGE_ROWS = 15;
export const NEXT_PAGE_ROWS = 26;

/** The count of an index in a mode: every track (Total count), the net across `line`, the most in view at once. */
export const countFor = (index, mode, line) => {
	if (mode === 'line') return lineCrossings(index, line).net;
	if (mode === 'max') return index.maxInView;
	return index.uniqueSeries[index.nFrames - 1];
};

/**
 * The steps, in the order the page applies its filters: every track, Sensitivity, the corrections, Size, the report.
 * stages: useCountModel's { doc, sensitivity, base }; index: the kept tracks; total: the report's number. Each middle
 * step is the change it makes, so the steps always add up to `total`.
 */
export const countSteps = ({ mode, line, stages, index, total, sensitivityLabel, sizeLabel }) => {
	const all = countFor(indexTracks(stages.doc), mode, line);
	const sensitive = countFor(
		indexTracks(filterByConfidence(stages.doc, stages.sensitivity)),
		mode,
		line
	);
	const corrected = countFor(stages.base || index, mode, line);
	return [
		{ label: 'Found by the analysis', value: all },
		{ label: sensitivityLabel, change: sensitive - all },
		{ label: 'Corrections', change: corrected - sensitive },
		{ label: sizeLabel, change: total - corrected },
		{ label: 'This report', value: total, end: true },
	];
};

/**
 * The edits that count (corrections.countingEdits, the rule of the count panel's summary), as rows in video time
 * order: { id, sec, time, kind, change, track, how }. Times are clip times, as the player and Your changes show them.
 */
export const correctionRows = (layer, doc, { subject, fps, follow = null }) => {
	const edits = layer?.edits || [];
	const { counts } = countingEdits(doc, layer);
	const firstFrame = new Map((doc?.tracks || []).map(tr => [tr.id, tr.t?.[0]]));
	const sec = f => f / fps;
	// whole seconds rounded, as the Your changes cards write them
	const at = f => clock(sec(f || 0), 0);
	return edits
		.map((e, k) => ({ e, place: k + 1 }))
		.filter(({ e }) => counts(e))
		.map(({ e, place }) => {
			const frame = e.frame ?? firstFrame.get(e.tracks?.[0]);
			return {
				id: e.id,
				place,
				sec: frame == null ? Infinity : sec(frame),
				time: frame == null ? '—' : clock(sec(frame)),
				...editRow(e, { subject, at, follow, place }),
			};
		})
		.sort((a, b) => a.sec - b.sec || a.place - b.place);
};

/** The rows in pages: `first` under the summary, then `next` on each page of the table alone. */
export const pageRows = (rows, first = FIRST_PAGE_ROWS, next = NEXT_PAGE_ROWS) => {
	const pages = [rows.slice(0, first)];
	for (let i = first; i < rows.length; i += next) pages.push(rows.slice(i, i + next));
	return pages;
};
